/**
 * Marketplace Service
 * Fetches sites from the marketplace API for the Browse Sites page.
 */

import axios from 'axios';
import { apiClient } from '@/services/_shared/apiClient';
import ENDPOINTS from '@/utils/endpoints';
import { ENV } from '@/config/env';

// `axios` (default import) is retained ONLY for the `axios.isAxiosError`
// type guard — it's a pure static helper with no interceptor side effects.
// All actual HTTP calls go through `apiClient`.

/* ── API response types (match backend shape) ── */

export interface ApiSeoMetrics {
  domain_authority: number;
  page_authority: number;
  domain_rating: number;
  spam_score: number;
  backlinks: number;
  organic_traffic: number;
  monthly_traffic: number;
  trust_flow: number;
  citation_flow: number;
}

export interface ApiPricing {
  guest_post: number;
  link_insertion: number;
  visual_link: number;
  currency: string;
}

export interface ApiGreyNichePrice {
  id: string;
  grey_niche_id: string;
  grey_niche: { id: string; name: string; slug: string };
  selling_price: number;
}

export interface ApiServiceInfo {
  turnaround_time_days: number;
  offers_guest_posts: boolean;
  offers_link_insertion: boolean;
  offers_visual_link: boolean;
}

export interface ApiQualityMetrics {
  approval_rate: number;
  on_time_delivery_rate: number;
  client_satisfaction_rate: number;
}

export interface ApiSite {
  id: string;
  domain: string;
  /** URL prefix returned by API: e.g. "https://" or "https://www.". */
  domain_type?: string;
  name: string;
  description: string;
  category: string | { id: string; slug: string; name: string };
  niche: string | { id: string; slug: string; name: string };
  tags: string[];
  primary_language: string;
  leading_countries: string[];
  country: { id: string; code: string; name: string } | null;
  language: { id: string; code: string; name: string } | null;
  seo_metrics: ApiSeoMetrics;
  pricing: ApiPricing;
  grey_niche_prices: ApiGreyNichePrice[];
  link_type: { id: string; slug: string; name: string } | null;
  link_validity: { id: string; slug: string; name: string } | null;
  maximum_link_count: number;
  mark_sponsored: boolean;
  service_info: ApiServiceInfo;
  quality_metrics?: ApiQualityMetrics;
  performance?: { avg_load_time: number; uptime_percentage: number };
  status: string;
  verification_status: string;
  last_verified_at: string | null;
  created_at: string;
}

export interface MarketplaceParams {
  page?: number;
  per_page?: number;
  search?: string;
  category?: string;
  niche?: string;
  language?: string;
  da_min?: number;
  da_max?: number;
  dr_min?: number;
  dr_max?: number;
  spam_score_max?: number;
  organic_traffic_min?: number;
  guest_post_price_min?: number;
  guest_post_price_max?: number;
  link_insertion_price_min?: number;
  link_insertion_price_max?: number;
  visual_link_price_min?: number;
  visual_link_price_max?: number;
  offers_guest_posts?: boolean;
  offers_link_insertion?: boolean;
  offers_visual_link?: boolean;
  max_turnaround_days?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

export interface MarketplaceResponse {
  sites: ApiSite[];
  pagination: {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
  };
  has_subscription?: boolean;
}

/** Thrown when a non-subscribed user tries to access page 2+. */
export class SubscriptionRequiredError extends Error {
  constructor() {
    super('Subscription required to view more sites');
    this.name = 'SubscriptionRequiredError';
  }
}

export interface MarketplaceFilters {
  categories: string[];
  niches: string[];
  languages: string[];
  price_ranges: {
    guest_post: { min: number; max: number };
    link_insertion: { min: number; max: number };
  };
  seo_ranges: {
    domain_authority: { min: number; max: number };
    domain_rating: { min: number; max: number };
  };
}

/**
 * Fetch marketplace sites with optional filters and pagination.
 */
export async function fetchMarketplaceSites(
  params: MarketplaceParams = {}
): Promise<MarketplaceResponse> {
  const queryParams: Record<string, string | number | boolean> = {};

  if (params.page) queryParams.page = params.page;
  if (params.per_page) queryParams.per_page = params.per_page;
  if (params.search) queryParams.search = params.search;
  if (params.category && params.category !== 'all') queryParams.category = params.category;
  if (params.niche) queryParams.niche = params.niche;
  if (params.language) queryParams.language = params.language;
  if (params.da_min !== undefined) queryParams.da_min = params.da_min;
  if (params.da_max !== undefined) queryParams.da_max = params.da_max;
  if (params.dr_min !== undefined) queryParams.dr_min = params.dr_min;
  if (params.dr_max !== undefined) queryParams.dr_max = params.dr_max;
  if (params.spam_score_max !== undefined) queryParams.spam_score_max = params.spam_score_max;
  if (params.organic_traffic_min !== undefined)
    queryParams.organic_traffic_min = params.organic_traffic_min;
  if (params.guest_post_price_min !== undefined)
    queryParams.guest_post_price_min = params.guest_post_price_min;
  if (params.guest_post_price_max !== undefined)
    queryParams.guest_post_price_max = params.guest_post_price_max;
  if (params.link_insertion_price_min !== undefined)
    queryParams.link_insertion_price_min = params.link_insertion_price_min;
  if (params.link_insertion_price_max !== undefined)
    queryParams.link_insertion_price_max = params.link_insertion_price_max;
  if (params.visual_link_price_min !== undefined)
    queryParams.visual_link_price_min = params.visual_link_price_min;
  if (params.visual_link_price_max !== undefined)
    queryParams.visual_link_price_max = params.visual_link_price_max;
  if (params.offers_guest_posts !== undefined)
    queryParams.offers_guest_posts = params.offers_guest_posts;
  if (params.offers_link_insertion !== undefined)
    queryParams.offers_link_insertion = params.offers_link_insertion;
  if (params.offers_visual_link !== undefined)
    queryParams.offers_visual_link = params.offers_visual_link;
  if (params.max_turnaround_days !== undefined)
    queryParams.max_turnaround_days = params.max_turnaround_days;
  if (params.sort_by) queryParams.sort_by = params.sort_by;
  if (params.sort_order) queryParams.sort_order = params.sort_order;

  const queryString = new URLSearchParams(
    Object.entries(queryParams).map(([k, v]) => [k, String(v)])
  ).toString();
  const fullUrl = ENDPOINTS.MARKETPLACE_SITES + (queryString ? `?${queryString}` : '');
  const curl = `curl -X GET "${fullUrl}" \\\n  -H "Authorization: Bearer {token}" \\\n  -H "X-App-Source: ${ENV.APP_SOURCE}"`;
  console.log(`[GET ${ENDPOINTS.MARKETPLACE_SITES}] curl:\n${curl}`);
  let response;
  try {
    response = await apiClient.get(ENDPOINTS.MARKETPLACE_SITES, {
      params: queryParams,
    });
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      console.error(
        `[GET ${ENDPOINTS.MARKETPLACE_SITES}] ERROR status:`,
        err.response?.status,
        'body:',
        err.response?.data
      );
    }
    // Detect subscription gate: 403 with subscription_required flag
    if (
      axios.isAxiosError(err) &&
      err.response?.status === 403 &&
      err.response?.data?.subscription_required
    ) {
      throw new SubscriptionRequiredError();
    }
    throw err;
  }
  console.log(`[GET ${ENDPOINTS.MARKETPLACE_SITES}] response:`, response.data);

  // Response shape: { status, message, data: [...sites], pagination: {...}, has_subscription }
  const payload = response.data;

  const sites: ApiSite[] = Array.isArray(payload?.data) ? payload.data : [];

  const pagination = {
    current_page: payload?.pagination?.current_page ?? params.page ?? 1,
    per_page: payload?.pagination?.per_page ?? params.per_page ?? 25,
    total: payload?.pagination?.total ?? sites.length,
    last_page: payload?.pagination?.last_page ?? 1,
  };

  return { sites, pagination, has_subscription: payload?.has_subscription };
}

/** Normalize filter arrays — API may return strings or {id,slug,name} objects */
function toStringArray(arr: unknown[]): string[] {
  return arr.map((item) =>
    typeof item === 'object' && item !== null && 'name' in item
      ? (item as { name: string }).name
      : String(item)
  );
}

/**
 * Fetch available filter options for the marketplace.
 */
export async function fetchMarketplaceFilters(): Promise<MarketplaceFilters> {
  const response = await apiClient.get(ENDPOINTS.MARKETPLACE_FILTERS);
  const raw = response.data?.data;

  if (!raw) {
    return {
      categories: [],
      niches: [],
      languages: [],
      price_ranges: {
        guest_post: { min: 0, max: 5000 },
        link_insertion: { min: 0, max: 3000 },
      },
      seo_ranges: {
        domain_authority: { min: 0, max: 100 },
        domain_rating: { min: 0, max: 100 },
      },
    };
  }

  return {
    categories: Array.isArray(raw.categories) ? toStringArray(raw.categories) : [],
    niches: Array.isArray(raw.niches) ? toStringArray(raw.niches) : [],
    languages: Array.isArray(raw.languages) ? toStringArray(raw.languages) : [],
    price_ranges: raw.price_ranges ?? {
      guest_post: { min: 0, max: 5000 },
      link_insertion: { min: 0, max: 3000 },
    },
    seo_ranges: raw.seo_ranges ?? {
      domain_authority: { min: 0, max: 100 },
      domain_rating: { min: 0, max: 100 },
    },
  };
}
